import type { TAnyClientQuestionType } from '@hubertai/screening-question-types';
import { sortOptions } from 'rtk/questionEditorThunks';

// label/enabled/replies are all per-language (TemplateCustomQuestionOptionsDocument) — merge each
// language key individually rather than spreading templateOpt wholesale, or an override for one
// language would silently wipe every other language's value for that same field.
export function mergeOptionsWithTemplateParams(
  questionOptions: any[] = [],
  templateOptions: Record<string, any> = {},
  language: string,
) {
  const allOptionIds = new Set([...questionOptions.map((opt) => opt.id), ...Object.keys(templateOptions)]);
  return Array.from(allOptionIds).map((optionId) => {
    const baseOpt = questionOptions.find((opt) => opt.id === optionId) || { id: optionId };
    const templateOpt = templateOptions[optionId] ?? {};
    const enabled = { ...(baseOpt.enabled || {}) };
    if (templateOpt.enabled && language in templateOpt.enabled) {
      enabled[language] = templateOpt.enabled[language];
    }
    const replies = { ...(baseOpt.replies || {}) };
    if (templateOpt.replies && language in templateOpt.replies) {
      replies[language] = templateOpt.replies[language];
    }
    const label = { ...(baseOpt.label || {}) };
    if (templateOpt.label && language in templateOpt.label) {
      label[language] = templateOpt.label[language];
    }
    return {
      ...baseOpt,
      ...(templateOpt.evaluation !== undefined && { evaluation: templateOpt.evaluation }),
      ...(templateOpt.bonus_points !== undefined && { bonus_points: templateOpt.bonus_points }),
      enabled,
      replies,
      label,
    };
  });
}

export function applyTemplateOptionsToQuestion(
  questionConfig: TAnyClientQuestionType,
  templateOptions: Record<string, any> | undefined,
  language: string,
): TAnyClientQuestionType {
  if (!('options' in questionConfig) || !Array.isArray(questionConfig.options)) {
    return questionConfig;
  }

  if (!templateOptions || typeof templateOptions !== 'object') {
    return questionConfig;
  }

  const options = sortOptions(
    mergeOptionsWithTemplateParams(questionConfig.options, templateOptions, language),
    templateOptions,
  );

  return { ...questionConfig, options };
}
